import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomNoPermissionView from "@/components/desktop/permission/no-permission/CustomNoPermissionView";
import { useBulkRefreshCodes } from "@/hooks/inventories/useBulkRefreshCodes";
import useUserStore from "@/stores/useUserStore";

import styles from "./modal-bulk-refresh-codes.module.scss";

function parseInventoryIdsFromWorkbook(workbook) {
	const firstSheetName = workbook.SheetNames[0];
	const worksheet = workbook.Sheets[firstSheetName];
	const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
	const inventoryIds = [];

	for (const row of rows) {
		const rawValue = row?.[0];
		if (rawValue === undefined || rawValue === null || rawValue === "") {
			continue;
		}

		const normalizedValue = String(rawValue).trim();
		if (!/^\d+$/.test(normalizedValue)) {
			continue;
		}

		const inventoryId = Number.parseInt(normalizedValue, 10);
		if (inventoryId > 0) {
			inventoryIds.push(inventoryId);
		}
	}

	return [...new Set(inventoryIds)];
}

const ModalBulkRefreshCodes = ({ onClose }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const fileInputRef = useRef(null);
	const user = useUserStore((state) => state.user);
	const isSuperUser = Number(user?.superuser || 0) === 1;

	const [selectedFileName, setSelectedFileName] = useState("");
	const [inventoryIds, setInventoryIds] = useState([]);
	const [result, setResult] = useState(null);

	const { mutate: bulkRefresh, isLoading } = useBulkRefreshCodes();

	const handleClose = useCallback(() => {
		if (typeof onClose === "function") {
			onClose();
			return;
		}

		const updatedSearchParams = new URLSearchParams(location.search);
		updatedSearchParams.delete("tool");

		navigate(
			{
				pathname: location.pathname,
				search: updatedSearchParams.toString() ? `?${updatedSearchParams.toString()}` : "",
				hash: location.hash,
			},
			{ replace: true },
		);
	}, [location.hash, location.pathname, location.search, navigate, onClose]);

	const previewText = useMemo(() => {
		if (!inventoryIds.length) {
			return "";
		}

		const previewLimit = 30;
		const previewIds = inventoryIds.slice(0, previewLimit).join(", ");
		const remainingCount = inventoryIds.length - previewLimit;

		if (remainingCount > 0) {
			return `${previewIds} … (+${remainingCount} más)`;
		}

		return previewIds;
	}, [inventoryIds]);

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}

		setResult(null);

		try {
			const arrayBuffer = await file.arrayBuffer();
			const workbook = XLSX.read(arrayBuffer, { type: "array" });
			const parsedInventoryIds = parseInventoryIdsFromWorkbook(workbook);

			if (!parsedInventoryIds.length) {
				setSelectedFileName("");
				setInventoryIds([]);
				toast.error("No se encontraron IDs validos en la primera columna del Excel");
				return;
			}

			setSelectedFileName(file.name);
			setInventoryIds(parsedInventoryIds);
			toast.success(`${parsedInventoryIds.length} IDs listos para recalcular`);
		} catch {
			setSelectedFileName("");
			setInventoryIds([]);
			toast.error("No se pudo leer el archivo Excel");
		} finally {
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleSubmit = () => {
		if (!inventoryIds.length) {
			toast.error("Sube un Excel con al menos un ID de inventario");
			return;
		}

		bulkRefresh(inventoryIds, {
			onSuccess: (response) => {
				const refreshResult = response?.data || null;
				setResult(refreshResult);

				if (!refreshResult) {
					toast.success("Recalculo completado");
					return;
				}

				if (refreshResult.failed > 0) {
					toast.warning(`Recalculo finalizado: ${refreshResult.successful} exitosos, ${refreshResult.failed} con error`);
					return;
				}

				toast.success(`Recalculo completado para ${refreshResult.successful} codigos`);
			},
			onError: (error) => {
				const message = error?.response?.data?.error || "No se pudo ejecutar el recalculo masivo";
				toast.error(message);
			},
		});
	};

	if (!isSuperUser) {
		return (
			<CustomModalForm open onClose={handleClose} title="Acceso restringido" size="md" showSave={false}>
				<div className={styles.noPermission}>
					<CustomNoPermissionView />
				</div>
			</CustomModalForm>
		);
	}

	return (
		<CustomModalForm
			open
			onClose={handleClose}
			title="Recalcular consumo masivo"
			size="lg"
			textForm="Recalcular"
			onSubmit={handleSubmit}
			isLoading={isLoading}
			processingMessage="Recalculando consumo"
			processingDescription="Esto puede tardar varios minutos segun la cantidad de IDs."
		>
			<div className={styles.container}>
				<p className={styles.description}>
					Sube un Excel con una sola columna de IDs de inventario (`inventories.id`). Cada fila se procesara con refresh code contra Lumen.
				</p>

				<input
					ref={fileInputRef}
					id="bulk-refresh-codes-file"
					className={styles.fileInput}
					type="file"
					accept=".xlsx,.xls"
					onChange={handleFileChange}
				/>
				<label className={styles.fileLabel} htmlFor="bulk-refresh-codes-file">
					{selectedFileName || "Seleccionar archivo Excel"}
				</label>

				{!!inventoryIds.length && (
					<div className={styles.summary}>
						<span>IDs detectados: {inventoryIds.length}</span>
						<span className={styles.previewIds}>{previewText}</span>
					</div>
				)}

				{!!result && (
					<div className={styles.summary}>
						<span className={styles.summarySuccess}>Exitosos: {result.successful}</span>
						<span className={result.failed > 0 ? styles.summaryError : styles.summarySuccess}>Con error: {result.failed}</span>
						{!!result.errors?.length && (
							<ul className={styles.errorsList}>
								{result.errors.map((errorItem) => (
									<li key={`${errorItem.inventory_id}-${errorItem.message}`}>
										ID {errorItem.inventory_id}: {errorItem.message}
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</div>
		</CustomModalForm>
	);
};

export default ModalBulkRefreshCodes;
